import { useEffect, useRef, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@racoon/ui";
import { IcCancel } from "@racoon/icons";

interface PhotoSliderProps {
  title?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  photos: string[];
  initialSlide?: number;
  onSlideChange?: (index: number) => void;
}

/**
 * Fullscreen photo slider shown in a dialog. Mirrors the admin
 * LocationPhotosSlider, adapted to govdash's plain URL photo lists.
 */
export const PhotoSlider = ({
  title,
  open,
  onOpenChange,
  photos,
  initialSlide = 0,
  onSlideChange,
}: PhotoSliderProps) => {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(initialSlide);

  // Jump to the clicked photo whenever the slider (re)opens.
  useEffect(() => {
    if (open && carouselApi) {
      setCurrent(initialSlide);
      carouselApi.scrollTo(initialSlide, true);
    }
  }, [open, initialSlide, carouselApi]);

  // Kept in a ref so a caller passing an inline arrow doesn't resubscribe the
  // carousel listener on every render.
  const onSlideChangeRef = useRef(onSlideChange);
  useEffect(() => {
    onSlideChangeRef.current = onSlideChange;
  }, [onSlideChange]);

  useEffect(() => {
    if (!carouselApi) return;
    setCurrent(carouselApi.selectedScrollSnap());
    const onSelect = () => {
      const index = carouselApi.selectedScrollSnap();
      setCurrent(index);
      onSlideChangeRef.current?.(index);
    };
    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] md:w-[60rem] md:max-w-[60rem] !p-3 sm:!p-4 gap-4 bg-white rounded-2xl max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] overflow-auto"
        showCloseButton={false}
      >
        <div className="flex items-center justify-between w-full">
          {title && (
            <DialogTitle className="text-base font-semibold text-text-primary leading-4 tracking-[-0.16px]">
              {title}
            </DialogTitle>
          )}
          <button
            type="button"
            aria-label="Close"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors border-none bg-transparent p-0"
          >
            <IcCancel className="w-8 h-8 text-text-primary" />
          </button>
        </div>

        <div className="relative w-full rounded-lg overflow-hidden max-h-[75vh]">
          <Carousel
            setApi={setCarouselApi}
            className="w-full h-full"
            opts={{ startIndex: initialSlide }}
          >
            <CarouselContent className="h-full">
              {photos.map((url, index) => (
                <CarouselItem key={`${url}-${index}`} className="relative h-full pl-0">
                  <div className="relative w-full h-full min-h-[240px] sm:min-h-[320px] flex items-center justify-center bg-black rounded-lg overflow-hidden">
                    <div
                      className="max-w-full max-h-full aspect-[4/3] w-full rounded-lg bg-contain bg-center bg-no-repeat"
                      style={{ backgroundImage: `url(${url})` }}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

            {photos.length > 1 && (
              <div className="absolute bottom-[50%] left-2 right-2 flex justify-between items-center z-10 pointer-events-none">
                <CarouselPrevious
                  className="relative left-0 top-0 translate-y-0 bg-white border border-border-primary w-6 h-6 p-2 rounded-lg pointer-events-auto hover:bg-muted"
                  variant="outline"
                  size="icon"
                />
                <CarouselNext
                  className="relative right-0 top-0 translate-y-0 bg-white border border-border-primary w-6 h-6 p-2 rounded-lg pointer-events-auto hover:bg-muted"
                  variant="outline"
                  size="icon"
                />
              </div>
            )}

            {photos.length > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center justify-center gap-4 z-10">
                {photos.map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === current ? "bg-brand-blue shadow-lg" : "bg-white"
                    }`}
                  />
                ))}
              </div>
            )}
          </Carousel>
        </div>
      </DialogContent>
    </Dialog>
  );
};
